<template>
  <div
    :class="[
      embedded ? 'lcf-embed' : 'lcf-dock',
      { 'lcf-dock-expanded': !embedded && expanded, 'lcf-dock-collapsed': !embedded && !expanded }
    ]"
  >
    <!-- Modo flutuante: botão minimizado (legado, mantido como fallback) -->
    <button
      v-if="!embedded && !expanded"
      type="button"
      class="lcf-launcher"
      :class="{ 'lcf-launcher-active': hasNewEvents }"
      @click="expand"
      :aria-label="`Live feed: ${visibleFeed.length} leituras`"
    >
      <span class="lcf-launcher-icon">
        <i class="bx bx-rss"></i>
      </span>
      <span v-if="visibleFeed.length > 0" class="lcf-launcher-badge">
        {{ visibleFeed.length > 99 ? '99+' : visibleFeed.length }}
      </span>
      <span v-if="hasNewEvents" class="lcf-launcher-pulse"></span>
    </button>

    <!-- Painel (flutuante expandido OU embutido) -->
    <div v-if="embedded || expanded" class="lcf-panel">
      <header class="lcf-head">
        <div class="lcf-head-left">
          <div class="lcf-status">
            <span class="lcf-conn-dot" :class="connectionStatusClass"></span>
            <span class="lcf-conn-label">{{ connectionStatusLabel }}</span>
          </div>
          <h6 class="lcf-head-title">Leituras ao vivo</h6>
        </div>
        <div class="lcf-head-actions">
          <button
            type="button"
            class="lcf-icon-btn"
            :class="{ 'lcf-icon-btn-active': liveFeedPaused }"
            @click="$emit('toggle-pause')"
            :title="liveFeedPaused ? 'Retomar' : 'Pausar'"
          >
            <i :class="liveFeedPaused ? 'bx bx-play' : 'bx bx-pause'"></i>
          </button>
          <button
            type="button"
            class="lcf-icon-btn"
            @click="$emit('clear')"
            :disabled="liveFeed.length === 0"
            title="Limpar tudo"
          >
            <i class="bx bx-trash"></i>
          </button>
          <button
            v-if="!embedded"
            type="button"
            class="lcf-icon-btn lcf-icon-close"
            @click="collapse"
            title="Minimizar"
          >
            <i class="bx bx-minus"></i>
          </button>
        </div>
      </header>

      <div class="lcf-body">
        <div v-if="visibleFeed.length === 0" class="lcf-empty">
          <div class="lcf-empty-pulse">
            <span class="lcf-empty-ring"></span>
            <span class="lcf-empty-ring"></span>
            <i class="bx bx-broadcast"></i>
          </div>
          <p>Aguardando leituras…</p>
          <small v-if="!liveFeedConnected">
            Sem conexão com o servidor de tempo real. Verifique configuração do Pusher.
          </small>
          <small v-else>
            As leituras vão aparecer aqui assim que acontecerem.
          </small>
        </div>

        <transition-group
          v-else
          tag="ul"
          class="lcf-list"
          name="lcf-card"
        >
          <li
            v-for="(item, idx) in visibleFeed"
            :key="item.id || (item._localKey + ':' + item.event_date)"
            class="lcf-row"
            :class="[
              `lcf-row-${item.event_type === 'CheckedIn' ? 'in' : 'out'}`,
              { 'lcf-row-fresh': idx === 0 && isVeryRecent(item) }
            ]"
          >
            <span class="lcf-row-icon">
              <i :class="item.event_type === 'CheckedIn' ? 'bx bx-log-in-circle' : 'bx bx-log-out-circle'"></i>
            </span>
            <div class="lcf-row-body">
              <div class="lcf-row-top">
                <span class="lcf-row-name" :title="item.participant_name || 'Anônimo'">
                  {{ item.participant_name || 'Anônimo' }}
                </span>
                <span class="lcf-row-time">{{ relativeTime(item.event_date) }}</span>
              </div>
              <div class="lcf-row-meta">
                <span v-if="item.tag_name" class="lcf-row-tag">
                  <i class="bx bx-purchase-tag-alt"></i>{{ item.tag_name }}
                </span>
                <span class="lcf-row-action">
                  {{ item.event_type === 'CheckedIn' ? 'entrou' : 'saiu' }}
                </span>
                <span v-if="item.operator_name" class="lcf-row-operator">
                  por {{ item.operator_name }}
                </span>
              </div>
            </div>
          </li>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'live-checkin-feed',
  props: {
    liveFeed: { type: Array, default: () => [] },
    liveFeedConnected: { type: Boolean, default: false },
    liveFeedPaused: { type: Boolean, default: false },
    showCheckout: { type: Boolean, default: true },
    embedded: { type: Boolean, default: false }
  },
  data: () => ({
    expanded: true,
    tick: 0,
    tickInterval: null,
    lastSeenCount: 0
  }),
  computed: {
    visibleFeed () {
      if (this.showCheckout) return this.liveFeed
      // Quando saídas estão ocultas, filtra do feed (defesa em profundidade
      // — o backend não vai mais broadcast Saídas no .env do user, mas
      // garantimos que eventos antigos do array não vazem).
      return this.liveFeed.filter(e => e.event_type === 'CheckedIn')
    },
    hasNewEvents () {
      return this.visibleFeed.length > this.lastSeenCount
    },
    connectionStatusClass () {
      if (this.liveFeedPaused) return 'lcf-conn-paused'
      if (this.liveFeedConnected) return 'lcf-conn-online'
      return 'lcf-conn-offline'
    },
    connectionStatusLabel () {
      if (this.liveFeedPaused) return 'pausado'
      if (this.liveFeedConnected) return 'ao vivo'
      return 'desconectado'
    }
  },
  watch: {
    expanded (val) {
      if (val) this.lastSeenCount = this.visibleFeed.length
    }
  },
  mounted () {
    this.tickInterval = setInterval(() => { this.tick++ }, 1000)
  },
  beforeDestroy () {
    if (this.tickInterval) clearInterval(this.tickInterval)
  },
  methods: {
    expand () {
      this.expanded = true
      this.lastSeenCount = this.visibleFeed.length
    },
    collapse () {
      this.expanded = false
      this.lastSeenCount = this.visibleFeed.length
    },
    isVeryRecent (item) {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      if (!item.event_date) return false
      const t = new Date(item.event_date.replace ? item.event_date.replace(' ', 'T') : item.event_date).getTime()
      return Date.now() - t < 5000
    },
    relativeTime (datetime) {
      // eslint-disable-next-line no-unused-expressions
      this.tick
      if (!datetime) return '—'
      const t = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime).getTime()
      const diff = Math.max(0, Math.floor((Date.now() - t) / 1000))
      if (diff < 5) return 'agora'
      if (diff < 60) return `há ${diff}s`
      const m = Math.floor(diff / 60)
      if (m < 60) return `há ${m}min`
      const h = Math.floor(m / 60)
      return `há ${h}h`
    }
  }
}
</script>

<style lang="scss" scoped>
.lcf-dock {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 9999;
  font-family: inherit;
}

/* Modo embutido: ocupa o card-slot inteiro do grid pai (60/40 ao lado do hero). */
.lcf-embed {
  font-family: inherit;
  height: 100%;
  /* Ancoramos o painel em absolute (abaixo). Assim a LISTA não empurra a altura
     do card: ele fica fixo no tamanho da linha do grid (alinhado ao hero) e as
     leituras antigas ficam escondidas/scrolláveis dentro do corpo, em vez de
     estourar o card pra baixo quando entram muitas. min-height garante altura
     também no layout empilhado (mobile), onde não há hero pra esticar a linha. */
  position: relative;
  min-height: 220px;

  .lcf-panel {
    position: absolute;
    inset: 0;
    width: 100%;
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
    animation: none;
  }

  .lcf-head {
    background: #fff;
    padding: 16px 20px 12px;
  }

  .lcf-body {
    background: #fff;
    padding: 4px 12px 12px;
    /* Sem scroll aqui: as leituras novas entram no topo e empurram as antigas
       pra fora do card, que ficam simplesmente clipadas (escondidas). O card
       mantém tamanho fixo e mostra só as que cabem. */
    overflow: hidden;
  }
}

/* ─── Launcher (collapsed) ─────────────────────────────────────────── */
.lcf-launcher {
  position: relative;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, #556ee6 0%, #8b5cf6 100%);
  color: #fff;
  border: 0;
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 24px rgba(85, 110, 230, 0.32);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(85, 110, 230, 0.42);
  }

  &.lcf-launcher-active {
    box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.25),
                0 8px 24px rgba(85, 110, 230, 0.45);
  }
}

.lcf-launcher-icon {
  font-size: 22px;
  display: grid;
  place-items: center;
}

.lcf-launcher-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #f46a6a;
  color: #fff;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 800;
  display: grid;
  place-items: center;
  border: 2px solid #fff;
  font-variant-numeric: tabular-nums;
}

.lcf-launcher-pulse {
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 2px solid #8b5cf6;
  animation: lcf-launcher-ping 1.4s ease-out infinite;
  pointer-events: none;
}

@keyframes lcf-launcher-ping {
  0%   { transform: scale(0.95); opacity: 0.7; }
  100% { transform: scale(1.5); opacity: 0; }
}

/* ─── Panel (expanded) ─────────────────────────────────────────────── */
.lcf-panel {
  width: 360px;
  max-width: calc(100vw - 32px);
  max-height: 70vh;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(26, 29, 38, 0.18);
  border: 1px solid #eaecf2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: lcf-panel-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes lcf-panel-in {
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.lcf-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #fafbfc;
  border-bottom: 1px solid #eaecf2;
  color: #1a1d26;
  flex-shrink: 0;
}

.lcf-head-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lcf-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #8b90a5;
}

.lcf-conn-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #b0b5c8;

  &.lcf-conn-online {
    background: #34c38f;
    box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.7);
    animation: lcf-conn-pulse 1.6s ease-out infinite;
  }
  &.lcf-conn-offline { background: #f46a6a; }
  &.lcf-conn-paused  { background: #f1b44c; }
}

@keyframes lcf-conn-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.7); }
  70%  { box-shadow: 0 0 0 8px rgba(52, 195, 143, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0); }
}

.lcf-conn-online + .lcf-conn-label { color: #34c38f; }

.lcf-head-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0;
}

.lcf-head-actions {
  display: flex;
  gap: 4px;
}

.lcf-icon-btn {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  color: #4a5068;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.15s ease;

  &:hover {
    background: #f5f6fa;
    color: #556ee6;
    border-color: #d4d8e3;
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  &.lcf-icon-btn-active {
    background: rgba(241, 180, 76, 0.14);
    color: #b8801f;
    border-color: rgba(241, 180, 76, 0.4);
  }

  &.lcf-icon-close:hover {
    background: rgba(244, 106, 106, 0.1);
    color: #d04848;
    border-color: rgba(244, 106, 106, 0.3);
  }
}

/* ─── Body ─────────────────────────────────────────────────────────── */
.lcf-body {
  flex: 1;
  min-height: 0; /* permite o overflow rolar dentro do flex em vez de esticar o card */
  overflow-y: auto;
  padding: 8px;
  background: #fafbfc;

  /* Scrollbar custom — fina e discreta */
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb {
    background: #d0d5dd;
    border-radius: 99px;
  }
  &::-webkit-scrollbar-track { background: transparent; }
}

.lcf-empty {
  padding: 36px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #8b90a5;
  gap: 6px;

  p { font-size: 13px; font-weight: 600; margin: 0; color: #4a5068; }
  small { font-size: 11px; max-width: 240px; line-height: 1.5; }
}

.lcf-empty-pulse {
  position: relative;
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  margin-bottom: 6px;

  i {
    font-size: 24px;
    color: #556ee6;
    z-index: 1;
  }
}

.lcf-empty-ring {
  position: absolute;
  inset: 0;
  border: 2px solid #556ee6;
  border-radius: 50%;
  opacity: 0;
  animation: lcf-ring 2s ease-out infinite;

  &:nth-child(2) { animation-delay: 1s; }
}

@keyframes lcf-ring {
  0%   { transform: scale(0.5); opacity: 0.5; }
  100% { transform: scale(1.4); opacity: 0; }
}

/* ─── Lista de eventos ─────────────────────────────────────────────── */
.lcf-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lcf-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #d4d8e3;
    box-shadow: 0 2px 8px rgba(26, 29, 38, 0.04);
  }

  /* Glow nos cards muito recentes */
  &.lcf-row-fresh {
    box-shadow: 0 0 0 2px rgba(85, 110, 230, 0.15),
                0 4px 14px rgba(85, 110, 230, 0.18);
    animation: lcf-fresh-glow 2s ease-out;
  }
}

@keyframes lcf-fresh-glow {
  0%   { background: rgba(85, 110, 230, 0.08); }
  100% { background: #fff; }
}

.lcf-row-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  font-size: 18px;
  flex-shrink: 0;
}

.lcf-row-in .lcf-row-icon {
  background: rgba(52, 195, 143, 0.12);
  color: #34c38f;
}

.lcf-row-out .lcf-row-icon {
  background: rgba(241, 180, 76, 0.12);
  color: #f1b44c;
}

.lcf-row-body {
  flex: 1;
  min-width: 0;
}

.lcf-row-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.lcf-row-name {
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.lcf-row-time {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.lcf-row-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #4a5068;
  margin-top: 2px;
  flex-wrap: wrap;
}

.lcf-row-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  background: #eef0f6;
  border-radius: 99px;
  color: #1a1d26;
  font-weight: 600;
  font-size: 10px;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  i { font-size: 11px; color: #556ee6; flex-shrink: 0; }
}

.lcf-row-action {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 600;
  text-transform: lowercase;
}

.lcf-row-operator {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 500;
}

/* ─── Transições da transition-group ──────────────────────────────── */
.lcf-card-enter-active {
  transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.lcf-card-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
  position: absolute;
  width: calc(100% - 16px);
}
.lcf-card-enter {
  opacity: 0;
  transform: translateY(-12px) scale(0.95);
}
.lcf-card-leave-to {
  opacity: 0;
  transform: translateX(-30px) scale(0.95);
}
.lcf-card-move {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (max-width: 640px) {
  .lcf-dock { right: 12px; bottom: 12px; }
  .lcf-panel { width: calc(100vw - 24px); }
}
</style>
